import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Leaf } from "lucide-react";
import SEO from "../components/SEO";

export default function Auth() {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  // login
  const [le, setLe] = useState("");
  const [lp, setLp] = useState("");
  // signup
  const [se, setSe] = useState("");
  const [sp, setSp] = useState("");
  const [sn, setSn] = useState("");
  const [hc, setHc] = useState(false);
  const [verifyLink, setVerifyLink] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(le, lp);
      toast.success(`Bienvenue, ${u.name || u.email}`);
      if (u.role === "admin") navigate("/admin");
      else if (u.role === "assistant") navigate("/assistant");
      else navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!hc) { toast.error("Veuillez confirmer que vous n'êtes pas un robot"); return; }
    setLoading(true);
    try {
      const r = await signup({ email: se, password: sp, name: sn, human_check: hc });
      toast.success("Compte créé. Vérifiez votre email pour activer votre compte.");
      if (r.verification_link_dev) setVerifyLink(r.verification_link_dev);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'inscription");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-12 bg-[#F9FAF6]" data-testid="auth-page">
      <SEO pageSlug="auth" title="Connexion" description="Connectez-vous à votre compte Herbalife Shop ou créez-en un en quelques secondes." path="/auth" noindex />
      <div className="w-full max-w-md bg-white border border-[#E5EAE5] rounded-3xl p-8 lg:p-10">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-[#7AC143] flex items-center justify-center text-white font-black">H</div>
          <span className="font-black text-lg">Herbalife<span className="text-[#7AC143]">Shop</span></span>
        </div>
        <h1 className="text-3xl font-black tracking-tighter mt-4">Bienvenue</h1>
        <p className="text-[#4B634B] mt-1 text-sm">Connectez-vous ou créez un compte pour commander.</p>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="grid grid-cols-2 w-full bg-[#F2F5F0]">
            <TabsTrigger value="login" data-testid="auth-tab-login">Connexion</TabsTrigger>
            <TabsTrigger value="signup" data-testid="auth-tab-signup">Inscription</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4 mt-6">
              <div>
                <Label htmlFor="le">Email</Label>
                <Input id="le" type="email" value={le} onChange={e => setLe(e.target.value)} required data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="lp">Mot de passe</Label>
                <Input id="lp" type="password" value={lp} onChange={e => setLp(e.target.value)} required data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full py-6 font-bold" data-testid="login-submit">
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
              <div className="text-center">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-[#4B634B] hover:text-[#7AC143] underline">
                  Mot de passe oublié ?
                </Link>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignup} className="space-y-4 mt-6">
              <div>
                <Label htmlFor="sn">Nom complet</Label>
                <Input id="sn" value={sn} onChange={e => setSn(e.target.value)} required data-testid="signup-name" />
              </div>
              <div>
                <Label htmlFor="se">Email</Label>
                <Input id="se" type="email" value={se} onChange={e => setSe(e.target.value)} required data-testid="signup-email" />
              </div>
              <div>
                <Label htmlFor="sp">Mot de passe</Label>
                <Input id="sp" type="password" value={sp} onChange={e => setSp(e.target.value)} required minLength={6} data-testid="signup-password" />
              </div>
              <label className="flex items-start gap-3 p-3 border border-[#E5EAE5] rounded-xl cursor-pointer hover:bg-[#F2F5F0]">
                <Checkbox checked={hc} onCheckedChange={setHc} data-testid="signup-human-check" className="mt-0.5" />
                <span className="text-sm flex items-center gap-2"><Leaf className="w-4 h-4 text-[#7AC143]" /> Je suis humain (vérification)</span>
              </label>
              <Button type="submit" disabled={loading} className="w-full bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full py-6 font-bold" data-testid="signup-submit">
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
              {verifyLink && (
                <div className="text-xs bg-[#FACC15]/10 border border-[#FACC15]/40 rounded-xl p-3" data-testid="signup-verify-link-dev">
                  <p className="font-semibold text-[#1A2E1A] mb-1">Email de vérification envoyé.</p>
                  <p className="text-[#4B634B]">En mode test, vous pouvez aussi cliquer ici : <a className="underline text-[#7AC143] break-all" href={verifyLink}>{verifyLink}</a></p>
                </div>
              )}
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
